import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  Input,
  output,
  OutputRefSubscription,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import {
  jsonDeepClone,
  OutsideClickDirective,
  SiaI18nService,
  SiaParamsAndPatternsService,
  SiaSelectBoxDataService,
  SiaSessionParam,
  SiaTooltipDirective,
  SiaTranslatePipe,
} from '@sia/shared';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subscription } from 'rxjs';
import { SelectBoxOptions } from '../../interfaces/select-box-options';
import { SiaBaseEditorComponent } from '../sia-base-editor/sia-base-editor.component';
import { SiaErrorHintComponent } from '../sia-error-hint/sia-error-hint.component';

@Component({
  selector: 'select-box-multi',
  templateUrl: './select-box-multi.component.html',
  styleUrls: ['./select-box-multi.component.scss'],
  standalone: true,
  imports: [
    NgxMatSelectSearchModule,
    OutsideClickDirective,
    SiaErrorHintComponent,
    SiaTooltipDirective,
    ReactiveFormsModule,
    SiaTranslatePipe,
    ScrollingModule,
    MatIconButton,
    MatFormField,
    FormsModule,
    MatSelect,
    MatOption,
    MatLabel,
    MatHint,
    MatIcon,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectBoxMultiComponent extends SiaBaseEditorComponent {
  #widgetDataService = inject(SiaSelectBoxDataService);
  #SiaParamsAndPatternsService = inject(SiaParamsAndPatternsService);
  #siaI18nService = inject(SiaI18nService);
  #hostElement = inject(ElementRef<HTMLElement>);

  #subs: (Subscription | OutputRefSubscription)[] = [];
  #selectBoxOptions: SelectBoxOptions = {};
  #timeouts: any[] = [];
  #dataSource: any[] = [];
  #inputString: string = '';
  #destroyed = false;
  #filteredDataSource: any[] = [];
  dataSourceLoaded = output<boolean>();
  #dataSourceLoaded = signal<boolean>(false);
  #dataSourceLoadedEffect = effect(() => {
    this.dataSourceLoaded.emit(this.#dataSourceLoaded());
  });
  #firstMultipleDataNotified: boolean = false;
  // Stable ordering map for selected items in MatSelect trigger
  #idOrderMap: Map<any, number> = new Map<any, number>();

  override set disabled(v: any) {
    super.disabled = v;
    if (this._isDisabled) {
      this.selectbox?.close();
    }
  }
  override get disabled() {
    return super.disabled;
  }

  selectFilterCtrl: FormControl = new FormControl();
  firstMultipleDataLoaded = output<any[]>();
  showVisibilityButton: boolean = false;
  isMobile?: boolean | null;
  showAll: boolean = false;
  isOpen: boolean = false;
  isAdmin?: boolean;

  selectionDescriptionChanged = output<string | undefined>();
  #scrollHandler: (event: Event) => void = () => {};
  #scrollTargets: EventTarget[] = [];
  readonly #scrollListenerCapture = true;
  #selectPanelElement?: HTMLElement | null;
  #visualViewportHeightBaseline?: number;
  #closeSuppressedUntil = 0;
  readonly #keyboardViewportDiffThreshold = 80;
  readonly #isIOS =
    typeof navigator !== 'undefined' &&
    /iP(ad|hone|od)/i.test((navigator.userAgent ?? '').toLowerCase());

  readonly MAX_VIEWPORT_HEIGHT = 209;
  readonly viewportItemSize = 35;
  get viewportHeight(): number {
    const len = this.#filteredDataSource?.length ?? 0;
    if (!len) return 0;
    const total = len * this.viewportItemSize;
    return Math.min(total, this.MAX_VIEWPORT_HEIGHT);
  }

  get hiddenSelectedOptions(): any[] {
    const values = Array.isArray(this._value) ? this._value : [];
    if (!values?.length) return [];
    const selectedIds = new Set(
      values.map((v: any) => (typeof v === 'object' ? v.id : v)),
    );
    const filtered = this.#filteredDataSource ?? [];

    // Build a map from id -> index in filteredDataSource
    const idToIndex = new Map<any, number>();
    filtered.forEach((x: any, i: number) => idToIndex.set(x?.id, i));

    // Helper: current rendered range in the viewport
    const getRenderedRange = () => {
      const vp: any = this.cdkVirtualScrollViewPort as any;
      if (!vp) return undefined as { start: number; end: number } | undefined;
      const range = vp.getRenderedRange?.();
      if (
        range &&
        typeof range.start === 'number' &&
        typeof range.end === 'number'
      )
        return range as { start: number; end: number };
      return undefined;
    };
    const range = getRenderedRange();
    const isRendered = (idx: number) =>
      range ? idx >= range.start && idx < range.end : false;
    return (this.#dataSource ?? []).filter((item: any) => {
      const id = item?.id;
      if (!selectedIds.has(id)) return false;
      const idx = idToIndex.has(id) ? (idToIndex.get(id) as number) : -1;
      if (idx === -1) return true;
      return !isRendered(idx);
    });
  }

  get selectBoxOptions(): SelectBoxOptions {
    return this.#selectBoxOptions;
  }

  get filteredDataSource(): any[] {
    return this.#filteredDataSource;
  }

  get dataSource(): any[] {
    return this.#dataSource;
  }
  override get value(): any {
    return this._value;
  }

  @ViewChild(CdkVirtualScrollViewport, { static: false })
  cdkVirtualScrollViewPort?: CdkVirtualScrollViewport;
  @ViewChild(MatSelect, { static: false }) selectbox?: MatSelect;

  set filteredDataSource(value: any[]) {
    // Clear previous filtered data source
    if (this.#filteredDataSource) {
      this.#filteredDataSource.length = 0;
    }

    if (
      !this.showAll &&
      value &&
      value.length > 0 &&
      this.showVisibilityButton
    ) {
      this.#filteredDataSource = value.filter((x) => !x.disabled);
      if (this.value?.length) {
        const selectedIds = new Set(
          (Array.isArray(this._value) ? this._value : []).map((v: any) =>
            typeof v === 'object' ? v.id : v,
          ),
        );
        selectedIds.forEach((id) => {
          if (!this.#filteredDataSource.some((x) => x?.id === id)) {
            const obj = (this.#dataSource ?? []).find((x) => x?.id === id);
            if (obj) this.#filteredDataSource.push(obj);
          }
        });
      }
    } else {
      // Use slice() for proper array cloning to avoid reference issues
      this.#filteredDataSource = value ? value.slice() : [];
    }

    // Refresh stable ordering map to keep trigger chips order consistent
    this.#refreshIdOrderMap(this.#filteredDataSource);

    // Update virtual scroll viewport when data size changes
    setTimeout(() => {
      try {
        this.cdkVirtualScrollViewPort?.checkViewportSize();
      } catch {}
    }, 0);
    this.cdr.markForCheck();
  }

  set dataSource(value: any[]) {
    const newValueJson = JSON.stringify(value);
    const currentValueJson = JSON.stringify(this.#dataSource);
    if (currentValueJson === newValueJson) {
      // No real change: avoid reprocessing to prevent UI flicker
      this.cdr.markForCheck();
      return;
    }

    // Clear previous data completely to help garbage collection
    this.clearDataSource();

    // Set new data with proper array cloning
    this.#dataSource = value ? value.slice() : [];
    // Check if data has disabled property for visibility toggle
    this.showVisibilityButton =
      value &&
      value.length > 0 &&
      value[0]?.hasOwnProperty('disabled') &&
      typeof value[0]['disabled'] === 'boolean';

    this.processDataSource();
    this.cdr.markForCheck();
  }

  // Comparator used by MatSelect to keep selected values in a stable order
  selectedOrderComparator = (
    a: import('@angular/material/core').MatOption,
    b: import('@angular/material/core').MatOption,
    _allSelected?: import('@angular/material/core').MatOption[],
  ): number => {
    const av =
      a?.value && typeof a.value === 'object'
        ? ((a.value as any)?.id ?? a.value)
        : a?.value;
    const bv =
      b?.value && typeof b.value === 'object'
        ? ((b.value as any)?.id ?? b.value)
        : b?.value;
    const ai = this.#idOrderMap.get(av) ?? Number.MAX_SAFE_INTEGER;
    const bi = this.#idOrderMap.get(bv) ?? Number.MAX_SAFE_INTEGER;
    return ai - bi;
  };

  // Build/refresh the id -> index map based on the current filtered list
  #refreshIdOrderMap(list: any[] | undefined): void {
    this.#idOrderMap.clear();
    if (!list || list.length === 0) return;
    for (let i = 0; i < list.length; i++) {
      const id = list[i]?.id;
      // Only set if id is defined, keep first occurrence to ensure stability
      if (id !== undefined && !this.#idOrderMap.has(id)) {
        this.#idOrderMap.set(id, i);
      }
    }
  }

  private clearDataSource(): void {
    // Clear arrays properly to help garbage collection
    if (this.#dataSource) {
      this.#dataSource.length = 0;
    }
    if (this.#filteredDataSource) {
      this.#filteredDataSource.length = 0;
    }
  }

  @Input()
  override set value(val: any) {
    // Convert undefined/null to empty array for multi-select consistency
    const normalizedVal = val === undefined || val === null ? [] : val;
    if (!Array.isArray(normalizedVal)) return;

    // Normalize values to ids and compare as sets (order-insensitive)
    const toIdArray = (v: any): any[] =>
      (Array.isArray(v) ? v : [])
        .map((x) => (typeof x === 'object' ? x?.id : x))
        .filter((x) => x !== undefined && x !== null);

    const prevIds = toIdArray(this._value);
    const nextIds = toIdArray(normalizedVal);

    // Create sets for comparison (handles duplicates and order-insensitivity)
    const prevIdSet = new Set(prevIds.map((id) => String(id)));
    const nextIdSet = new Set(nextIds.map((id) => String(id)));

    // Check if sets have same size and contain same elements
    const eq =
      prevIdSet.size === nextIdSet.size &&
      Array.from(prevIdSet).every((id) => nextIdSet.has(id));

    if (eq) return;

    this._value = jsonDeepClone(normalizedVal);

    try {
      this.valueChanged.emit(this._value);
      this.valueChange.emit(this._value);
    } catch (error) {
      // Component might be destroyed, ignore the error
    }
  }

  @Input() set selectBoxOptions(options: SelectBoxOptions) {
    const needReload =
      JSON.stringify(this.#selectBoxOptions.fixedDataSource) !==
        JSON.stringify(options.fixedDataSource) ||
      this.#selectBoxOptions.dbKey !== options.dbKey ||
      this.#selectBoxOptions.foreignKey !== options.foreignKey;

    const valueChanged =
      options.value != null &&
      Array.isArray(options.value) &&
      JSON.stringify(options.value) !== JSON.stringify(this._value);

    if (!needReload && !valueChanged) {
      return;
    }

    if (needReload) {
      this.#dataSourceLoaded.set(false);
    }

    if (!options.orderByKey) {
      options.orderByKey = 'description';
    }
    if (valueChanged) {
      this.value = options.value;
    }

    this.#selectBoxOptions = jsonDeepClone({
      translateData: true,
      ...options,
    });
    if (needReload) {
      this.loadDataSource();
    }
    this.cdr.markForCheck();
  }

  @Input() set fixedDataSource(value: any[]) {
    if (
      JSON.stringify(this.#selectBoxOptions.fixedDataSource) ==
      JSON.stringify(value)
    ) {
      return;
    }
    this.#selectBoxOptions.fixedDataSource = value;
    this.loadDataSource();
    this.cdr.markForCheck();
  }

  @Input() set foreignKey(value: any) {
    this.#dataSourceLoaded.set(false);
    this.#dataSource = [];
    this.#selectBoxOptions.foreignKey = value;
    if (this.#selectBoxOptions.dbKey && this.#selectBoxOptions.foreignKey) {
      this.value = [];
      this._value = [];
      this.#selectBoxOptions.value = [];
    }
    this.loadDataSource();
    this.cdr.markForCheck();
  }

  filterElements() {
    if (!this.#dataSource?.length) {
      this.filteredDataSource = [];
      return;
    }

    const filterValue = this.selectFilterCtrl.value;
    if (!filterValue?.trim()) {
      this.filteredDataSource = this.#dataSource;
      return;
    }

    const lowerFilterValue = filterValue.toLowerCase();
    this.filteredDataSource = this.#dataSource.filter(
      (item) =>
        item.description &&
        item.description.toLowerCase().includes(lowerFilterValue),
    );
    this.cdr.markForCheck();
  }
  async loadDataSource(force: boolean = false) {
    if (this.selectBoxOptions.fixedDataSource && !this.#dataSourceLoaded()) {
      this.dataSource = jsonDeepClone(this.selectBoxOptions.fixedDataSource);
      this.filteredDataSource = jsonDeepClone(this.dataSource);
      this.#dataSourceLoaded.set(true);
    } else if (this.#selectBoxOptions.dbKey) {
      const res = await this.#widgetDataService.loadFullData(
        this.#selectBoxOptions.dbKey,
        this.#selectBoxOptions.foreignKey,
        false,
        true,
        this.#selectBoxOptions.useCache,
        force,
        this.#selectBoxOptions.orderByKey,
        this.#selectBoxOptions.orderByAsc,
      );
      this.#dataSourceLoaded.set(true);
      if (this.#selectBoxOptions.removeUser) {
        this.dataSource = res?.filter(
          (x: any) => x.id != this.siaUsersService.getUserData()?.id,
        );
      } else {
        this.dataSource = jsonDeepClone(res);
      }
    }
  }

  processDataSource() {
    if (
      !this.#dataSourceLoaded() ||
      (!this.#selectBoxOptions.dbKey && !this.#selectBoxOptions.fixedDataSource)
    ) {
      this.cdr.markForCheck();
      return;
    }
    if (!this.#dataSource || this.#dataSource.length == 0) {
      this.value = [];
      this.filteredDataSource = [];
      try {
        this.cdr.markForCheck();
      } catch (error) {}
      return;
    }
    if (
      this.#selectBoxOptions.removeNotFoundValue &&
      this._value &&
      this._value.length > 0 &&
      this.#dataSource &&
      this.#dataSource.length > 0
    ) {
      this._value = this._value.filter((v: any) => {
        return this.#dataSource.some((x: any) => x.id === v);
      });
      if (this._value.length === 0) {
        this.value = [];
      } else {
        this.value = this._value;
      }
    }

    const dataLen = this.#dataSource.length;
    const currentIds = Array.isArray(this._value)
      ? this._value
          .map((v: any) => (typeof v === 'object' ? v?.id : v))
          .filter((v: any) => v !== undefined && v !== null)
      : [];
    const hasValue = currentIds.length > 0;
    const valueNotFound = hasValue
      ? currentIds.every(
          (id: any) => !this.#dataSource.some((x: any) => x?.id == id),
        )
      : false;
    if (
      this.#selectBoxOptions.autoSelectFirst &&
      dataLen > 0 &&
      (!hasValue || valueNotFound)
    ) {
      const first = this.#dataSource[0];
      this.value = [first?.id];
    }
    if (
      this.#selectBoxOptions.autoSelectAll &&
      dataLen > 0 &&
      (!hasValue || valueNotFound)
    ) {
      this.value = this.#dataSource.map((x: any) => x.id);
    }
    this.filteredDataSource = this.#dataSource;
    if (this._value && this._value.length > 0) {
      this.processSelectedIds();
      this.cdr.markForCheck();
      return;
    }
    const ids = this.#dataSource
      .filter((x: any) => x.selected)
      .map((x: any) => x.id);
    if (JSON.stringify(ids) == JSON.stringify(this._value)) {
      this.cdr.markForCheck();
      return;
    }

    this.value = ids;
    this.cdr.markForCheck();
  }

  updateSqlView() {
    this.siaRouterService.navigateMenuWithParams('popup^db-editor', [
      { id: 'sqlObject', value: this.#selectBoxOptions.dbKey },
    ]);
  }

  async openChange($event: boolean) {
    this.isOpen = $event;
    this.cdr.markForCheck();
    this.cleanupScrollHandler();

    if (!this.#dataSource?.length) {
      await this.loadDataSource();
    }

    if ($event && this.cdkVirtualScrollViewPort) {
      let index = 0;
      const ids = new Set(
        (Array.isArray(this._value) ? this._value : []).map((v: any) =>
          typeof v === 'object' ? v.id : v,
        ),
      );
      if (ids.size > 0) {
        const found = this.filteredDataSource.findIndex(
          (x) => x && ids.has(x.id),
        );
        index = found >= 0 ? found : 0;
      }
      if (index >= 2) index = index - 2;
      try {
        this.cdkVirtualScrollViewPort.scrollToIndex(index);
        this.cdkVirtualScrollViewPort.checkViewportSize();
      } catch {}
    }

    if ($event) {
      // Only add event listener when opening
      const timeoutId = setTimeout(() => {
        // Check if component is still alive
        if (this.#destroyed || !this.selectbox) return;

        const overlay = document.querySelector(
          '.cdk-overlay-container .cdk-overlay-connected-position-bounding-box',
        );
        if (overlay) {
          const selectPanel = overlay.querySelector(
            '.mat-mdc-select-panel',
          ) as HTMLElement | null;
          if (selectPanel) {
            // Find the backdrop that belongs to this specific select overlay
            const overlayPane = overlay.parentElement;
            const backdrop = overlayPane?.querySelector(
              '.cdk-overlay-backdrop:not(.cdk-overlay-dark-backdrop)',
            ) as HTMLElement;
            if (backdrop && overlayPane?.contains(selectPanel)) {
              // Only hide the select backdrop, not dialog backdrops
              backdrop.style.display = 'none';
            }

            this.#setupExternalScrollHandling(selectPanel);
          }
        }
      }, 0);
      this.#timeouts.push(timeoutId);
    } else {
      this.cleanupScrollHandler();
    }
  }

  closeSelectOptions(): void {
    // Close the select box
    this.isOpen = false;
    this.selectbox?.close();
    this.cleanupScrollHandler();

    // Update selected descriptions when closing
    this.processSelectedIds();

    // Only trigger change detection if necessary and component is still alive
    try {
      this.cdr.markForCheck();
    } catch (error) {
      // Component might be destroyed, ignore the error
    }
  }

  private cleanupScrollHandler(): void {
    const capture = this.#scrollListenerCapture;
    this.#scrollTargets.forEach((target) => {
      try {
        target.removeEventListener('scroll', this.#scrollHandler, capture);
      } catch {}
    });
    this.#scrollTargets = [];
    this.#selectPanelElement = undefined;
    this.#visualViewportHeightBaseline = undefined;
    this.#closeSuppressedUntil = 0;
  }

  #setupExternalScrollHandling(selectPanel: HTMLElement): void {
    this.cleanupScrollHandler();

    if (typeof window === 'undefined') {
      return;
    }

    this.#selectPanelElement = selectPanel;
    const viewport = window.visualViewport;
    this.#visualViewportHeightBaseline = viewport?.height ?? window.innerHeight;
    this.#closeSuppressedUntil = 0;

    const hostElement = this.#hostElement.nativeElement;
    const candidates = this.#getScrollableAncestors(hostElement);
    const uniqueTargets = new Set<EventTarget>(candidates);

    if (typeof document !== 'undefined') {
      uniqueTargets.add(document);
      const scrollingElement = document.scrollingElement;
      if (scrollingElement) {
        uniqueTargets.add(scrollingElement);
      }
    }
    uniqueTargets.add(window);

    this.#scrollTargets = Array.from(uniqueTargets);

    const listenerOptions = {
      capture: this.#scrollListenerCapture,
      passive: true,
    } as const;

    this.#scrollHandler = (event: Event) => {
      if (!this.isOpen) {
        return;
      }
      if (this.#isKeyboardScrollEvent(event)) {
        return;
      }
      if (!this.#shouldHandleExternalScroll(event)) {
        return;
      }
      if (this.#isCloseSuppressed()) {
        return;
      }
      this.closeSelectOptions();
    };

    this.#scrollTargets.forEach((target) => {
      try {
        target.addEventListener('scroll', this.#scrollHandler, listenerOptions);
      } catch {}
    });
  }

  #getScrollableAncestors(element: HTMLElement): EventTarget[] {
    const results: EventTarget[] = [];
    if (typeof document === 'undefined') {
      return results;
    }

    let current: HTMLElement | null = element.parentElement;
    while (current) {
      if (this.#isElementScrollable(current)) {
        results.push(current);
      }
      if (current === document.body || current === document.documentElement) {
        break;
      }
      current = current.parentElement;
    }

    return results;
  }

  #isElementScrollable(element: HTMLElement): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const style = window.getComputedStyle(element);
    const overflowValues = [style.overflow, style.overflowY, style.overflowX];
    const allowsScroll = overflowValues.some((value) =>
      ['auto', 'scroll', 'overlay'].includes(value),
    );
    if (!allowsScroll) {
      return false;
    }
    return (
      element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth
    );
  }

  onSearchFocus(): void {
    this.#suppressCloseTemporarily(this.getSuppressionDuration());
  }

  private getSuppressionDuration(): number {
    return this.#isIOS ? 1500 : 200;
  }

  #suppressCloseTemporarily(
    durationMs: number = this.getSuppressionDuration(),
  ): void {
    const expiry = Date.now() + durationMs;
    this.#closeSuppressedUntil = expiry;
    const timeout = setTimeout(() => {
      if (this.#closeSuppressedUntil === expiry) {
        this.#closeSuppressedUntil = 0;
      }
    }, durationMs);
    this.#timeouts.push(timeout);
  }

  #isCloseSuppressed(): boolean {
    return Date.now() < this.#closeSuppressedUntil;
  }

  #shouldHandleExternalScroll(event: Event): boolean {
    const target = event.target as EventTarget | null;

    if (!target) {
      return true;
    }

    if (target instanceof HTMLElement) {
      if (
        this.#selectPanelElement &&
        this.#selectPanelElement.contains(target)
      ) {
        return false;
      }
      if (
        this.#isIOS &&
        typeof document !== 'undefined' &&
        this.#isFocusInsideSelectPanel() &&
        (target === document.body || target === document.documentElement) &&
        this.#shouldIgnoreDocumentScroll()
      ) {
        return false;
      }
      const host = this.#hostElement.nativeElement;
      return target.contains(host);
    }

    if (typeof window !== 'undefined' && target === window) {
      return !this.#shouldIgnoreDocumentScroll();
    }

    if (typeof Document !== 'undefined' && target instanceof Document) {
      return !this.#shouldIgnoreDocumentScroll();
    }

    return true;
  }

  #shouldIgnoreDocumentScroll(): boolean {
    if (!this.#isIOS) {
      return false;
    }
    if (typeof window === 'undefined') {
      return false;
    }
    if (!this.#isFocusInsideSelectPanel()) {
      return false;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return false;
    }

    const currentHeight = viewport.height;
    if (this.#visualViewportHeightBaseline === undefined) {
      this.#visualViewportHeightBaseline = currentHeight;
      return false;
    }

    const diff = Math.abs(currentHeight - this.#visualViewportHeightBaseline);
    this.#visualViewportHeightBaseline = currentHeight;
    return diff > this.#keyboardViewportDiffThreshold;
  }

  #isFocusInsideSelectPanel(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }
    const panel = this.#selectPanelElement;
    if (!panel) {
      return false;
    }
    const active = document.activeElement;
    if (!active) {
      return false;
    }
    return panel.contains(active);
  }

  #isKeyboardScrollEvent(event: Event): boolean {
    if (!this.#isIOS) {
      return false;
    }
    if (!this.#isFocusInsideSelectPanel()) {
      return false;
    }
    if (!this.#shouldIgnoreDocumentScroll()) {
      return false;
    }

    if (event.target instanceof HTMLElement) {
      const host = this.#hostElement.nativeElement;
      if (event.target === host || host.contains(event.target)) {
        return true;
      }
    }

    if (typeof window !== 'undefined' && event.target === window) {
      return true;
    }

    if (typeof document !== 'undefined') {
      if (
        event.target === document.body ||
        event.target === document.documentElement
      ) {
        return true;
      }
    }

    if (typeof Document !== 'undefined' && event.target instanceof Document) {
      return true;
    }

    return true;
  }
  private processSelectedIds() {
    if (!this.dataSource?.length) {
      return;
    }
    this.#dataSource.forEach((item) => (item.selected = false));

    let msg = '';
    let first = true;
    if (this._value && this._value.length > 0) {
      this._value.forEach((item: any) => {
        const itemId = typeof item === 'object' ? item.id : item;
        const f = this.#dataSource.find((x) => x.id == itemId);

        if (f) {
          if (!first) {
            msg += ', ';
          } else {
            first = false;
          }
          // Translate the description if translateData flag is enabled
          const description = this.#selectBoxOptions.translateData
            ? this.#siaI18nService.translate(f.description)
            : f.description;
          msg += description;
          f.selected = true;
        }
      });
    }
    if (!this.#firstMultipleDataNotified) {
      this.firstMultipleDataLoaded.emit(this._value);
      this.#firstMultipleDataNotified = true;
    }
    this.selectionDescriptionChanged.emit(msg);
    this.cdr.markForCheck();
  }

  onKeyDown(e: KeyboardEvent) {
    if ((e.ctrlKey || (e as any).metaKey) && (e.key === 's' || e.key === 'S')) {
      return;
    }
    if (e.ctrlKey || e.key === 'Tab') {
      return;
    }
    if (this.isOpen) {
      // Keep the overlay from closing while typing within the search field.
      this.#suppressCloseTemporarily(this.getSuppressionDuration());
    }
    if (e.key === 'Backspace') {
      this.#inputString = this.#inputString.slice(0, -1);
    } else if (e.key.length === 1) {
      this.#inputString += e.key;
      this.#filteredDataSource.find((x) =>
        x.description.toLowerCase().startsWith(this.#inputString.toLowerCase()),
      )?.id;
    }
    this.cdr.markForCheck();
  }
  onBlur(e: FocusEvent) {
    this.#inputString = '';
  }

  showRadar() {
    this.navigateRadar(
      this.selectBoxOptions.gridId ?? '',
      this.selectBoxOptions.gridId,
      [],
      null,
      this._value,
    ).then((data) => {
      if (!data) {
        return;
      }
      this.value = data;
      this.cdr.markForCheck();
    });
  }

  async navigateRadar(
    menuId: string,
    menuMobileId?: string,
    params: SiaSessionParam[] = [],
    foreignKey?: any,
    selectedId?: any,
  ): Promise<any> {
    params.push({ id: 'selectionMode', value: 'multiple' });

    if (selectedId) {
      params.push({
        id: 'selectedids',
        value: [JSON.stringify({ id: selectedId })],
      });
    }
    if (foreignKey) {
      params.push({ id: '__foreignkey', value: foreignKey });
    }
    if (menuMobileId && this.siaSizeDetector.currentSize.isMobile) {
      this.siaRouterService.navigateMenuWithParams(
        'radar^' + menuMobileId,
        params,
      );
    } else {
      this.siaRouterService.navigateMenuWithParams('radar^' + menuId, params);
    }

    this._radarOpen = true;
    var promiseResolve: any, promiseReject;
    this._radarSelection = new Promise(function (resolve, reject) {
      promiseResolve = resolve;
      promiseReject = reject;
    });
    this.promiseResolver = promiseResolve;

    return this._radarSelection;
  }

  ngAfterViewInit() {
    let s0 = this.selectFilterCtrl.valueChanges.subscribe(() => {
      this.filterElements();
      if (this.isOpen) {
        this.#suppressCloseTemporarily(this.getSuppressionDuration());
        // Reset the active item to the first one (the search box option)
        // so that the next Down arrow key moves to the first filtered result.
        const tFocus = setTimeout(() => {
          (this.selectbox as any)?._keyManager?.setActiveItem(0);
        }, 1);
        this.#timeouts.push(tFocus);
      }
    });
    this.#subs.push(s0);
  }

  ngOnDestroy(): void {
    // Mark as destroyed first
    this.#destroyed = true;

    // Close the select box if open to trigger cleanup
    this.selectbox?.close();
    this.cleanupScrollHandler();

    // Unsubscribe from all subscriptions
    if (this.#subs && this.#subs.length > 0) {
      this.#subs.forEach((sub) => {
        try {
          sub.unsubscribe();
        } catch (e) {
          // Ignore unsubscribe errors
        }
      });
    }
    this.#subs = [];

    // Clear all timeouts
    if (this.#timeouts && this.#timeouts.length > 0) {
      this.#timeouts.forEach((timeout) => {
        try {
          clearTimeout(timeout);
        } catch (e) {
          // Ignore clear timeout errors
        }
      });
    }
    this.#timeouts = [];

    // Clear data arrays completely to help garbage collection
    this.clearDataSource();

    // Clean up form control properly
    if (this.selectFilterCtrl) {
      try {
        this.selectFilterCtrl.reset();
        // Don't destroy the form control as it might be used elsewhere
      } catch (e) {
        // Ignore form control cleanup errors
      }
    }

    // Clear radar selection promise
    if (this.promiseResolver) {
      try {
        this.promiseResolver(null);
      } catch (e) {
        // Ignore promise resolver errors
      }
    }
    this._radarSelection = undefined;
    this._radarOpen = false;

    // Clear input string
    this.#inputString = '';

    // Reset flags
    this.#dataSourceLoaded.set(false);
    this.showAll = false;
    this.showVisibilityButton = false;
    this.#firstMultipleDataNotified = false;

    // Call parent destroy
    super.ngOnDestroy();
  }
  ngOnInit() {
    super.ngOnInit();
    var promiseResolve: any, promiseReject;
    this._radarSelection = new Promise(function (resolve, reject) {
      promiseResolve = resolve;
      promiseReject = reject;
    });
    this.promiseResolver = promiseResolve;
    this.isMobile = this.siaSizeDetector.currentSize.isMobile;
    this.isAdmin = this.siaUsersService.getUserData()?.isAdmin;
    let s0 = this.valueChange.subscribe((value) => {
      this.processSelectedIds();
    });
    this.#subs.push(s0);
    let s1 = this.siaSignalrService.refreshCollectionsMessage$.subscribe(
      async (messageKeys: { [key: string]: boolean }) => {
        if (!this.#selectBoxOptions.dbKey) return;
        const dbKeyLower = this.#selectBoxOptions.dbKey.toLowerCase();
        const messageKeysLower = Object.keys(messageKeys).map((key) =>
          key.toLowerCase(),
        );

        if (
          messageKeysLower.some((key: string) => dbKeyLower.includes(key)) ||
          messageKeys['*']
        ) {
          if (this.isModifiedFlag) {
            return;
          }
          this.#dataSourceLoaded.set(false);
          this.value = [];
          this._value = [];
          this.#selectBoxOptions.value = [];
          await this.loadDataSource(true);
          this.processSelectedIds();
        }
      },
    );
    this.#subs.push(s1);

    let s = this.#SiaParamsAndPatternsService.departmentChanged$.subscribe(
      async () => {
        if (!this.#selectBoxOptions.dbKey) return;
        this.clearDataSource();
        this.#dataSourceLoaded.set(false);
        await this.loadDataSource(true);
      },
    );
    this.#subs.push(s);

    let s2 = this.siaRadarService
      .finalSelectionChanged$()
      .subscribe((selection: any) => {
        if (this._radarOpen) {
          this.promiseResolver(selection);
          this._radarOpen = false;
        }
      });
    this.#subs.push(s2);

    let s3 = this.siaRouterService.routeChanged$.subscribe(() => {
      this.selectbox?.close();
    });
    this.#subs.push(s3);
  }
  toggleVisibility() {
    this.showAll = !this.showAll;
    this.filterElements();
    this.cdr.markForCheck();
  }

  trackById(index: number, item: any): any {
    // Use item.id if available, otherwise fallback to index
    // This helps Angular optimize re-rendering of mat-option elements
    return item?.id !== undefined ? item.id : index;
  }
}
